import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Divider,
  Collapse,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useMap } from '../../contexts/MapContext';
import * as turf from '@turf/turf';

const InfoPanel: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { userLocation, gazaPosition, setIsInfoPanelExpanded } = useMap();
  const [expanded, setExpanded] = useState(!isMobile);

  // Update context when expansion changes
  useEffect(() => {
    if (isMobile) {
      setIsInfoPanelExpanded(expanded);
    }
  }, [expanded, isMobile, setIsInfoPanelExpanded]);
  const [stats, setStats] = useState({
    area: '365 km²',
    population: '2.3 million',
    density: '6,300/km²',
    length: '41 km',
    width: '6-12 km',
    distance: 0,
  });

  useEffect(() => {
    if (userLocation && gazaPosition && userLocation !== gazaPosition) {
      const from = turf.point(Array.isArray(userLocation) 
        ? [userLocation[1], userLocation[0]] 
        : [userLocation.lng, userLocation.lat]);
      const to = turf.point(Array.isArray(gazaPosition)
        ? [gazaPosition[1], gazaPosition[0]]
        : [gazaPosition.lng, gazaPosition.lat]);
      const distance = turf.distance(from, to, { units: 'kilometers' });
      setStats(prev => ({ ...prev, distance: Math.round(distance) }));
    }
  }, [userLocation, gazaPosition]);

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        top: isMobile ? 'auto' : 80,
        bottom: isMobile ? 0 : 'auto',
        left: isMobile ? 0 : 16,
        right: isMobile ? 0 : 'auto',
        width: isMobile ? '100%' : 320,
        maxHeight: isMobile ? '40vh' : 'auto',
        overflow: 'auto',
        zIndex: 1000,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: isMobile ? '16px 16px 0 0' : 2,
      }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Gaza Strip Statistics
        </Typography>
        {isMobile && (
          <IconButton 
            size="small" 
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? 'Collapse panel' : 'Expand panel'}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>

      <Collapse in={expanded}>
        <Divider />
        
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Geographic Constraints
          </Typography>
          
          <Box sx={{ mt: 1 }}>
            <StatItem label="Total Area" value={stats.area} />
            <StatItem label="Length" value={stats.length} />
            <StatItem label="Width" value={stats.width} />
            {stats.distance > 0 && (
              <StatItem 
                label="Overlay Distance" 
                value={`${stats.distance} km from your location`} 
              />
            )}
          </Box>
        </Box>

        <Divider />
        
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Population Impact
          </Typography>
          
          <Box sx={{ mt: 1 }}>
            <StatItem label="Population" value={stats.population} />
            <StatItem label="Density" value={stats.density} />
            <StatItem 
              label="Movement" 
              value="Restricted to boundaries" 
              highlight 
            />
          </Box>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="caption" color="text.secondary">
            Imagine living your entire life within these boundaries - unable to leave, 
            with limited resources and restricted movement.
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

interface StatItemProps {
  label: string;
  value: string;
  highlight?: boolean;
}

const StatItem: React.FC<StatItemProps> = ({ label, value, highlight }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <Typography 
      variant="body2" 
      sx={{ 
        fontWeight: highlight ? 600 : 400,
        color: highlight ? 'secondary.main' : 'text.primary',
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default InfoPanel;